import { useMemo, useState } from 'react';
import { estimateTricks, formatCallBreakScore, sortHand, type CallBreakLegal, type CallBreakView } from '@cardhub/engine';
import { PlayingCard } from '../cards/PlayingCard';
import { Hand } from '../cards/Hand';
import { Modal } from '../components/ui';
import { useT } from '../i18n';
import { useGame } from '../store/game';
import { seatPosition, SeatView, MySeat, useNarrow, useSpeech } from './common';

const OFFSETS: Record<string, { x: number; y: number }> = {
  bottom: { x: 0, y: 34 },
  left: { x: -62, y: 0 },
  top: { x: 0, y: -34 },
  right: { x: 62, y: 0 },
};

export function CallBreakTable({ scoresOpen, onCloseScores }: { scoresOpen: boolean; onCloseScores: () => void }) {
  const t = useT();
  const view = useGame((s) => s.view) as CallBreakView;
  const legal = useGame((s) => s.legal) as CallBreakLegal | null;
  const seats = useGame((s) => s.seats);
  const mySeat = useGame((s) => s.mySeat);
  const turn = useGame((s) => s.turn);
  const act = useGame((s) => s.act);
  const eventsId = useGame((s) => s.eventsId);
  const speech = useSpeech();
  const [selected, setSelected] = useState<string | null>(null);
  const hand = useMemo(() => sortHand(view.hand), [view.hand]);
  const playable = new Set(legal?.kind === 'play' ? legal.cards : []);
  const suggested = useMemo(() => Math.max(view.config.minBid, Math.min(view.config.maxBid, Math.round(estimateTricks(view.hand) - 0.2))), [view.hand, view.config]);
  const name = (s: number) => seats[s]?.user?.username ?? `Seat ${s + 1}`;
  const fmt = (v: number) => formatCallBreakScore(v, view.config.display);
  const myTurn = mySeat !== null && turn?.seats.includes(mySeat);
  const narrow = useNarrow();
  // Trick cards sit between the side seats; shrink them (and their spread) on phones.
  const trickSize = narrow ? 'md' : 'lg';
  const k = narrow ? 0.7 : 1;

  const onCard = (c: string) => {
    if (!myTurn || legal?.kind !== 'play' || !playable.has(c)) return;
    // Single tap plays a legal card (fast on mobile); illegal cards are dimmed and not tappable.
    setSelected(c);
    void act({ type: 'play', card: c }).finally(() => setSelected(null));
  };

  return (
    <>
      <div className="stage">
      <div className="felt" />
      {[0, 1, 2, 3].map((s) => (
        <SeatView
          key={s}
          seat={s}
          info={seats[s]}
          mySeat={mySeat}
          total={4}
          turn={turn}
          speech={speech[s]}
          sub={<span className="dim num">{fmt(view.totals[s] ?? 0)} pts</span>}
          tags={
            <>
              {view.dealer === s && <span className="tag">D</span>}
              {view.bids[s] !== null && <span className="tag">{t('cb.tricks', { won: view.tricksWon[s] ?? 0, bid: view.bids[s] ?? '-' })}</span>}
            </>
          }
        />
      ))}
      <div className="felt-center centered">
        <div className="center-note">
          {t('table.round', { n: view.round, total: view.config.rounds })}
          {view.phase === 'BIDDING' && !myTurn && turn?.seats[0] !== undefined && ` · ${t('table.waitingFor', { name: name(turn.seats[0]) })}`}
        </div>
        <div className={`trick ${view.phase === 'TRICK_END' ? 'collect' : ''}`} key={`trick-${view.round}-${view.tricks.length}-${view.phase === 'TRICK_END'}`} aria-label="Cards on the table">
          {view.trick.map((p, i) => {
            const dir = seatPosition(p.seat, mySeat, 4).dir;
            const o = OFFSETS[dir]!;
            const off = { x: o.x * k, y: o.y * k };
            const w = view.phase === 'TRICK_END' && view.lastTrick ? OFFSETS[seatPosition(view.lastTrick.winner, mySeat, 4).dir]! : null;
            const winnerDir = w ? { x: w.x * k, y: w.y * k } : null;
            return (
              <div
                key={`${p.card}-${eventsId > 0 ? i : i}`}
                className="slot fly"
                style={{
                  transform: `translate(calc(-50% + ${off.x}px), calc(-50% + ${off.y}px)) rotate(${(i - 1.5) * 4}deg)`,
                  ['--from-x' as string]: `${off.x * 3}px`,
                  ['--from-y' as string]: `${off.y * 4}px`,
                  ...(winnerDir ? { ['--to-x' as string]: `${winnerDir.x * 3}px`, ['--to-y' as string]: `${winnerDir.y * 4}px`, animationDelay: '0.45s' } : {}),
                  zIndex: i,
                }}
              >
                <PlayingCard id={p.card} size={trickSize} />
              </div>
            );
          })}
        </div>
        {view.phase === 'ROUND_END' && view.roundScores.length > 0 && (
          <div className="card glass" style={{ textAlign: 'center' }}>
            <strong>Round {view.round} scores</strong>
            <div className="row" style={{ justifyContent: 'center', marginTop: 6 }}>
              {view.roundScores.at(-1)!.map((sc, s) => (
                <span key={s} className={`pill ${sc >= 0 ? 'accent' : 'danger'}`}>
                  {name(s)}: {fmt(sc)}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>
      <MySeat
        seat={mySeat !== null ? seats[mySeat] : undefined}
        sub={
          mySeat !== null ? (
            <>
              {fmt(view.totals[mySeat] ?? 0)} pts{view.bids[mySeat] !== null ? ` · ${t('cb.tricks', { won: view.tricksWon[mySeat] ?? 0, bid: view.bids[mySeat] ?? '-' })}` : ''}
            </>
          ) : null
        }
      />
      </div>
      <div className="table-bottom">
        {myTurn && legal?.kind === 'bid' && <BidPicker min={legal.min} max={legal.max} suggested={suggested} onBid={(v) => void act({ type: 'bid', value: v })} />}
        <Hand size="lg" label="Your hand">
          {hand.map((c, i) => (
            <PlayingCard
              key={c}
              id={c}
              size="lg"
              dealIndex={view.phase === 'BIDDING' ? i : undefined}
              selected={selected === c}
              playable={myTurn && playable.has(c)}
              unplayable={myTurn && legal?.kind === 'play' && !playable.has(c)}
              onClick={() => onCard(c)}
            />
          ))}
        </Hand>
      </div>
      <Modal open={scoresOpen} onClose={onCloseScores} title={t('table.scores')}>
        <table className="scoreboard">
          <thead>
            <tr>
              <th>#</th>
              {[0, 1, 2, 3].map((s) => (
                <th key={s} className="ellipsis" style={{ maxWidth: 70 }}>{name(s)}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {view.roundScores.map((row, r) => (
              <tr key={r}>
                <td>R{r + 1}</td>
                {row.map((v, s) => (
                  <td key={s} className="num" style={{ color: v < 0 ? 'var(--danger)' : undefined }}>{fmt(v)}</td>
                ))}
              </tr>
            ))}
            <tr>
              <td><strong>Total</strong></td>
              {view.totals.map((v, s) => (
                <td key={s} className="num"><strong>{fmt(v)}</strong></td>
              ))}
            </tr>
          </tbody>
        </table>
      </Modal>
    </>
  );
}

/**
 * Bid picker: one row of big chips for the realistic range (1–8, "+" reveals the rest), the suggested
 * call marked with a star, and a separate confirm button so a stray tap never commits a call.
 */
function BidPicker({ min, max, suggested, onBid }: { min: number; max: number; suggested: number; onBid: (v: number) => void }) {
  const t = useT();
  const [value, setValue] = useState(suggested);
  const [more, setMore] = useState(suggested > 8);
  const [sent, setSent] = useState(false);
  const top = more ? max : Math.min(max, 8);
  const values = Array.from({ length: top - min + 1 }, (_, i) => min + i);
  return (
    <div className="bid-picker" role="group" aria-label={t('cb.bid')}>
      <div className="bid-head">
        <strong>{t('cb.yourCall')}</strong>
        <span className="dim">
          ★ {t('cb.suggested')}: <b>{suggested}</b>
        </span>
      </div>
      <div className={`bid-chips ${more ? 'more' : ''}`} role="radiogroup" aria-label={t('cb.bid')}>
        {values.map((v) => (
          <button
            key={v}
            role="radio"
            aria-checked={value === v}
            className={`bid-chip ${value === v ? 'on' : ''} ${v === suggested ? 'star' : ''}`}
            onClick={() => setValue(v)}
            aria-label={`${v}${v === suggested ? ` (${t('cb.suggested')})` : ''}`}
          >
            {v}
          </button>
        ))}
        {!more && max > 8 && (
          <button className="bid-chip plus" onClick={() => setMore(true)} aria-label={t('cb.moreBids')}>
            +
          </button>
        )}
      </div>
      <button
        className="btn primary block bid-confirm"
        disabled={sent}
        onClick={() => {
          setSent(true);
          onBid(value);
          setTimeout(() => setSent(false), 1500);
        }}
      >
        {t('cb.bidValue', { n: value })}
      </button>
    </div>
  );
}
